'use client'
import { useSearchParams } from 'next/navigation'
import { Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import ImageComponent from '../../common/ImageComponent'
import type { ISingleProductAPIResponseData } from '@/src/types/singleProduct'

interface SingleProductGalleryProps {
    data: ISingleProductAPIResponseData | null
}

export default function SingleProductGallery({ data }: SingleProductGalleryProps) {
    const searchParams = useSearchParams()

    const color = searchParams.get('color') ?? data?.variants?.[0]?.color ?? ''
    const size = searchParams.get('size') ?? data?.variants?.[0]?.size ?? ''

    // Try exact color+size match → color-only match → first variant
    const variant = (
        data?.variants?.find(
            v =>
                v.color?.toLowerCase() === color.toLowerCase() &&
                v.size?.toLowerCase() === size.toLowerCase()
        ) ??
        data?.variants?.find(
            v => v.color?.toLowerCase() === color.toLowerCase()
        ) ??
        data?.variants?.[0]
    ) ?? null
    // console.log('data', data);

    const isVariant = data?.is_variant || Boolean(variant);
    // console.log('isVariant', isVariant);

    const noVariantImages: string[] = [data?.thumbnail ?? '', ...(data?.images ?? [])];
    const activeImages: string[] =
        isVariant
            ? (variant?.images?.length ? variant?.images : noVariantImages) ?? []
            : (noVariantImages ?? [])
    // console.log('activeImages', activeImages);

    const carouselKey = `${variant?.sku ?? variant?.color ?? 'default'}-${activeImages.join('|')}`

    return (
        <>
            {/* Preload all other variant images so switching is instant */}
            {isVariant && (
                <div className="hidden" aria-hidden="true">
                    {data?.variants?.map((v, vi) =>
                        v.color?.toLowerCase() !== variant?.color?.toLowerCase()
                            ? v.images?.map((imgSrc, i) =>
                                imgSrc ? (
                                    <ImageComponent
                                        key={`preload-${vi}-${i}`}
                                        src={imgSrc}
                                        alt=""
                                        object_cover={true}
                                        loading="eager"
                                    />
                                ) : null
                            )
                            : null
                    )}
                </div>
            )}

            {activeImages.length > 0 ? (
                <>
                    <div className="relative aspect-4/5 mb-2 w-full overflow-hidden bg-secondary-background-color md:hidden">
                        <Swiper
                            key={carouselKey}
                            modules={[Pagination]}
                            slidesPerView={1}
                            spaceBetween={0}
                            pagination={
                                activeImages.length > 1
                                    ? { clickable: true, dynamicBullets: true, dynamicMainBullets: 5 }
                                    : false
                            }
                            className="single-product-gallery-mobile h-full w-full"
                        >
                            {activeImages.map((src: string, index: number) => (
                                <SwiperSlide key={`${src}-${index}`} className="h-full!">
                                    <div className="relative h-full w-full min-h-0">
                                        <ImageComponent
                                            src={src}
                                            alt={data?.title ?? ''}
                                            object_cover={true}
                                            priority={index === 0}
                                        />
                                    </div>
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    </div>

                    <div className="hidden md:contents">
                        {activeImages.map((src: string, index: number) => (
                            <div key={index} className="w-full min-h-[200px] bg-secondary-background-color">
                                <ImageComponent
                                    src={src}
                                    alt={data?.title ?? ''}
                                    object_cover={true}
                                    priority={index === 0}
                                />
                            </div>
                        ))}
                    </div>
                </>
            ) : null}
        </>
    )
}
